import { useEffect } from 'react';
import { useAsyncFn } from 'react-use';
import {
  GetDownloadLinkSDKResponse,
  MediaManagerSDKFile,
  RemoveFileSDKResponse,
} from '../../../../typings/media-manager-sdk';
import {
  FedopsInteractions,
  fedopsLogger,
} from '../../../services/fedopsLogger';
import { usePrivateSourceMethods } from './usePrivateSourceMethods';
import { IWixmpRequestParams } from './usePrivateWixmp.types';

export const VideoMakerPrivateMediaTag = '_videomaker';
export const MediaManagerAnyFolderId = 'any';

export const useWixmpSearchVideoMakerItems = ({
  mediaToken,
}: {
  mediaToken: string;
}) => {
  const { searchVideoMakerItems } = usePrivateSourceMethods(mediaToken);
  const [
    { loading: isLoading, value: wixmpResponse, error: requestError },
    initRequest,
  ] = useAsyncFn<{ data: MediaManagerSDKFile[]; status?: number }>(
    () => {
      fedopsLogger.interactionStarted(FedopsInteractions.MyVideosListLoaded);
      return searchVideoMakerItems();
    },
    [mediaToken],
    {
      loading: false,
      value: { data: [] },
    },
  );

  useEffect(() => {
    if (
      !isLoading &&
      wixmpResponse &&
      wixmpResponse.status &&
      wixmpResponse.status === 200
    ) {
      fedopsLogger.interactionEnded(FedopsInteractions.MyVideosListLoaded);
    }
  }, [isLoading]);

  return { isLoading, wixmpResponse, initRequest, requestError };
};

export const useWixmpGetFilesInfo = ({
  mediaToken,
}: {
  mediaToken: string;
}) => {
  const { getFilesInfo } = usePrivateSourceMethods(mediaToken);

  const [
    { loading: isLoading, value: wixmpResponse, error: requestError },
    initRequest,
  ] = useAsyncFn<{ data: MediaManagerSDKFile[] }>(
    (...filesIds: string[]) => getFilesInfo(filesIds),
    [mediaToken],
    {
      loading: false,
      value: { data: [] },
    },
  );

  return { isLoading, wixmpResponse, initRequest, requestError };
};

export const useWixmpGetDownloadLink = ({
  mediaToken,
  file,
}: IWixmpRequestParams) => {
  const { getDownloadLink } = usePrivateSourceMethods(mediaToken);
  const [
    { loading: isLoading, value: wixmpResponse, error },
    initRequest,
  ] = useAsyncFn<{ data: GetDownloadLinkSDKResponse }>(
    () => getDownloadLink(file.id),
    [mediaToken, file],
    {
      loading: false,
    },
  );

  return { isLoading, wixmpResponse, initRequest, error };
};

export const useWixmpDeleteFile = ({ mediaToken }: { mediaToken: string }) => {
  const { deleteFile } = usePrivateSourceMethods(mediaToken);
  const [
    { loading: isLoading, value: wixmpResponse, error },
    initRequest,
  ] = useAsyncFn<RemoveFileSDKResponse>(
    (...files: MediaManagerSDKFile[]) => deleteFile(files),
    [mediaToken],
    {
      loading: false,
    },
  );

  return { isLoading, wixmpResponse, initRequest, error };
};
